import { useStore } from '@/hooks/useStore';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ITEMS_CONST, ROUTES } from '@/configs/constants';
import { toastify } from '@/utils/toastify';
import Header from '@/components/layout/Header';
import { flowResult } from 'mobx';
import imgSend from '@/assets/images/pngs/NavigationArrow.png';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Textarea } from '@/components/common/Textarea';
import Title from '@/components/common/Title';
import { cn } from '@/utils/utils';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import UploadImage from '@/components/common/UploadImage';
import RadioGroupComponent from '@/components/common/RadioGroupHtml';

export default observer(function RegisterNewItem() {
    const {
        itemsStore: { registerNewItem, uploadFile },
        modalStore: { showAlertModal, hideModal }
    } = useStore();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const goBack = () => {
        navigate(ROUTES.collectefItems.href);
    };
    const [showInputCoupon, setShowInputCoupon] = useState<boolean>(false);
    const [fileSelected, setFileSelected] = useState<File | undefined>(undefined);
    const [msgFile, setMsgFile] = useState<boolean>(false);

    // Validation schema
    const validateSchema = yup.object().shape({
        file_id: yup.number(),
        title: yup.string().trim().required('form_error_validate.required').max(255, 'form_error_validate.max_255'),
        description: yup.string().trim().required('form_error_validate.required').max(1000, 'form_error_validate.max_1000'),
        itemType: yup.number().required('form_error_validate.required'),
        statusItem: yup.number().required('form_error_validate.required'),
        code: yup.string().trim().when('itemType', {
            is: (value: number) => value === ITEMS_CONST.ITEM_TYPE.COUPON,
            then: schema => schema.notRequired().trim(),
            otherwise: schema => schema.notRequired()
        }),
        fileName: yup.string().required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            file_id: undefined,
            title: '',
            description: '',
            itemType: ITEMS_CONST.ITEM_TYPE.NOT_SET,
            statusItem: ITEMS_CONST.STATUS.PUBLISHED,
            code: '',
            fileName: ''
        }
    });

    const handleSubmitData = async (formData: FormData) => {
        if (!fileSelected) {
            setMsgFile(true);
            return;
        }

        const res = await flowResult(uploadFile(fileSelected));
        if (res) {
            const resPost = await flowResult(registerNewItem({ ...formData, file_id: res }));
            if (resPost) {
                navigate(ROUTES.collectefItems.href);
            }
        }
    };

    const onUploadDone = async (file: File | undefined) => {
        form.setValue('fileName', file?.name || '');
        form.setError('fileName', { type: 'manual', message: '' });
        setMsgFile(false);
        setFileSelected(file);
    };

    return (
        <div className='w-screen h-screen flex flex-col justify-start bg-white pt-[72px]'>
            <Header title={t('text.editItem')} goBackFunction={goBack} />
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmitData)} className='mx-4 h-[calc(100vh-72px-24px)] flex flex-col justify-between'>
                    <div className='overflow-y-scroll scrollbar-hide pb-6'>
                        <Title
                            title='アイテム情報'
                            titleClassname='text-[15px] font-semibold text-[#333333]'
                        />
                        <FormField
                            control={form.control}
                            name='fileName'
                            render={({ field }) => (
                                <FormItem>
                                    <Title title={t('text.image')} require />
                                    <FormControl>
                                        <UploadImage
                                            className='mt-[6px]'
                                            isUpload
                                            onSelectedImage={onUploadDone}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='title'
                            render={({ field }) => (
                                <FormItem>
                                    <Title title='アイテム名' require />
                                    <FormControl>
                                        <Input {...field} className='bg-bgInput border-bgMenubar' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='description'
                            render={({ field }) => (
                                <FormItem>
                                    <Title title='アイテム説明' require />
                                    <FormControl>
                                        <Textarea maxLength={1000} {...field} className='mt-[6px] bg-bgInput border-bgMenubar w-full h-[35vw]' />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <Title title='アイテム種別' require />
                        <RadioGroupComponent
                            items={[
                                { value: ITEMS_CONST.ITEM_TYPE.NOT_SET.toString(), label: '設定なし' },
                                { value: ITEMS_CONST.ITEM_TYPE.COUPON.toString(), label: 'クーポン' }
                            ]}
                            className='mt-3'
                            onSelect={(e) => {
                                const itemType = parseInt(e);
                                form.setValue('itemType', itemType);
                                setShowInputCoupon(itemType === ITEMS_CONST.ITEM_TYPE.COUPON);
                                if (itemType !== ITEMS_CONST.ITEM_TYPE.COUPON) {
                                    form.setValue('code', '');
                                }
                            }}
                        />
                        {showInputCoupon && (
                            <FormField
                                control={form.control}
                                name='code'
                                render={({ field }) => (
                                    <FormItem className='w-full'>
                                        <Title title='クーポンコード' />
                                        <FormControl>
                                            <Input
                                                placeholder='クーポンコード'
                                                {...field}
                                                className='pt-[6px] bg-bgInput border-bgMenubar w-full'
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}
                        <Title title='ステータス' require />
                        <RadioGroupComponent
                            className='mt-3'
                            items={[
                                { value: ITEMS_CONST.STATUS.PUBLISHED.toString(), label: '公開' },
                                { value: ITEMS_CONST.STATUS.UN_PUBLISHED.toString(), label: '未公開' }
                            ]}
                            onSelect={(e) => {
                                form.setValue('statusItem', parseInt(e));
                            }}
                        />
                    </div>
                    <Button className='w-full' variant='main' type='submit'>
                        {t('text.register')}
                    </Button>
                </form>
            </Form>
        </div>
    );
});
